import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Upload, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import HeroClassic from "@/components/sections/HeroClassic";
import HeroDark from "@/components/sections/HeroDark";
import { useSkin } from "@/lib/skin";
import TrustBadges from "@/components/sections/TrustBadges";
import HowItWorksSection from "@/components/sections/HowItWorksSection";
import FaqAccordion from "@/components/sections/FaqAccordion";
import { FAQS, SERVICE_CITIES, BUSINESS } from "@/lib/constants";

const HERO_IMG = "https://images.pexels.com/photos/36729855/pexels-photo-36729855.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

export default function Home() {
  const skin = useSkin();
  return (
    <div data-testid="page-home">
      <SEO
        title="Carfornia Buyers — Sell Your Car in the Bay Area"
        description="Veteran-owned car buyer based in Hayward, CA. Request an offer online, we inspect your car in person and give you a real written offer. No pressure, no obligation."
        canonical="https://carforniabuyers.com/"
        image={HERO_IMG}
      />

      {skin === "dark" ? <HeroDark /> : <HeroClassic />}

      {/* Trust strip */}
      <section className="relative -mt-10 lg:-mt-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        <TrustBadges />
      </section>

      <HowItWorksSection />

      {/* Beat my offer */}
      <section className="py-20 lg:py-28 bg-[#0B1220] relative overflow-hidden" data-testid="beat-offer-section">
        <div className="absolute inset-0 opacity-20 bg-grain" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-brand-orange/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 text-white">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-bold uppercase tracking-[0.2em]">
              <Tag className="w-3.5 h-3.5 text-brand-orange" /> We pay more
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-4 leading-tight">
              Got a quote from the big guys? Let us beat it.
            </h2>
            <p className="text-slate-300 text-base sm:text-lg mt-5 leading-relaxed">
              We buy cars to resell them ourselves, not to flip them at auction. That is why we can often pay more than the national online buyers — on everyday cars, trucks and SUVs of every age and mileage.
            </p>
            <ul className="mt-6 space-y-2 text-slate-200">
              {["All makes, models, ages and mileages", "Clean, lien, salvage and rebuilt titles reviewed", "Same-day payment when you accept"].map((it) => (
                <li key={it} className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-brand-orange flex-shrink-0" /><span>{it}</span></li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="bg-white rounded-3xl p-7 sm:p-9 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)]" data-testid="beat-offer-card">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-orange-50 text-brand-orange mb-4"><Upload className="w-6 h-6" /></div>
              <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Already have a CarMax, Carvana, or other written offer?</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">Upload it and give us a chance to beat it. We’ll review your vehicle and let you know whether we can offer more — no obligation.</p>
              <Link to="/sell-your-car" state={{ beatOffer: true }}>
                <Button data-testid="beat-offer-cta" className="mt-6 h-13 px-7 bg-[#FF6B00] hover:bg-[#E66000] text-white font-bold rounded-xl w-full sm:w-auto">
                  Upload your offer <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <p className="text-xs text-slate-500 mt-4">Beating another offer is not guaranteed. Our purchase offer is subject to vehicle inspection and verification. <Link to="/compare" data-testid="home-compare-link" className="text-brand-blue font-semibold hover:underline">See how we compare to CarMax, Carvana and trade-in →</Link></p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Carfornia */}
      <section className="py-20 lg:py-28 bg-brand-surface" data-testid="why-carfornia">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Why Carfornia Buyers</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mt-2">
              A small business that does what it says.
            </h2>
            <p className="text-base sm:text-lg text-slate-600 mt-4 leading-relaxed">
              We are a veteran-owned car buyer based in Hayward, not a lead-generation marketplace. Your information stays with us, and the person who inspects your car is the person who makes the offer.
            </p>
            <Link to="/about" className="inline-flex items-center gap-2 mt-6 font-semibold text-brand-blue hover:gap-3 transition-all">
              About the business <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              { t: "A real offer, in writing", d: "We never quote a number online and change it at pickup. The offer comes after we see the car, and it comes in writing." },
              { t: "Straight talk on paperwork", d: "We walk you through the title transfer and the DMV release of liability so nothing is left hanging." },
              { t: "Local and accountable", d: "We are in Hayward and serve the Bay Area and surrounding areas. You can call the owner directly." },
              { t: "Loans and tricky titles", d: "Tell us about a loan, salvage or lost title up front and we will tell you honestly what we can do." },
            ].map((c) => (
              <div key={c.t} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all">
                <h3 className="font-display font-bold text-lg text-slate-900">{c.t}</h3>
                <p className="text-slate-600 mt-2 text-[15px] leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service areas teaser */}
      <section className="py-20 lg:py-28 bg-white" data-testid="areas-teaser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Service areas</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mt-2">
                Serving the Bay Area, surrounding areas — and Oahu.
              </h2>
            </div>
            <Link to="/service-areas" className="inline-flex items-center gap-2 font-semibold text-brand-blue hover:gap-3 transition-all">
              See all areas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {SERVICE_CITIES.slice(0, 7).map((c) => (
              <Link
                key={c.name}
                to="/service-areas"
                className="px-5 py-4 rounded-xl border border-slate-100 hover:border-brand-blue hover:bg-blue-50/40 transition-all group"
              >
                <div className="font-display font-bold text-slate-900 group-hover:text-brand-blue">{c.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">Bay Area</div>
              </Link>
            ))}
            <Link to="/service-areas" data-testid="home-hawaii-tile" className="px-5 py-4 rounded-xl border border-emerald-200 bg-emerald-50/50 hover:border-emerald-500 transition-all group">
              <div className="font-display font-bold text-slate-900 group-hover:text-emerald-700">Oahu, Hawaii</div>
              <div className="text-xs text-emerald-700 mt-0.5">Honolulu · Pearl City · Kapolei</div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-brand-surface" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Common questions</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mt-2">
              Everything you’re wondering.
            </h2>
          </div>
          <FaqAccordion items={FAQS.slice(0, 6)} />
          <div className="text-center mt-10">
            <Link to="/faq" className="inline-flex items-center gap-2 font-semibold text-brand-blue hover:gap-3 transition-all">
              Read all FAQs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-[#0B1220] relative overflow-hidden" data-testid="final-cta">
        <div className="absolute inset-0 opacity-20 bg-grain" />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-brand-blue/30 blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Ready when you are</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mt-3 leading-tight">
            Tell us about your car. We’ll take it from there.
          </h2>
          <p className="text-lg text-slate-300 mt-5 max-w-2xl mx-auto">
            It takes a couple of minutes, costs nothing, and you are never obligated to sell.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/sell-your-car">
              <Button data-testid="cta-final-offer" className="h-14 px-8 bg-[#FF6B00] hover:bg-[#E66000] text-white text-base font-bold rounded-xl shadow-lg shadow-orange-500/30">
                Request my offer <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href={BUSINESS.phoneHref}>
              <Button variant="outline" className="h-14 px-8 bg-white/5 hover:bg-white/10 text-white border-white/20 hover:border-white/40 font-bold rounded-xl">
                Call {BUSINESS.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
